import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { describeWindow, formatRate, IDLE_ALLOWANCE_MINUTES, priceSplit, tariffFields, type TariffDefaults, type TariffKind } from '@pulsepoint/shared';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm, useWatch, type Control } from 'react-hook-form';
import { z } from 'zod';
import { ApiError } from '../../api/client';
import { useChargers, useSetTariff, useTariffDefaults, type Charger } from '../../api/chargers';
import type { DialogProps } from '../../components/dialogs/DialogProvider';
import { FormDialog } from '../../components/dialogs/FormDialog';
import { applyApiError } from '../../components/form/apiErrors';
import { useToast } from '../../components/Toast';
import { formSaver, useUnsavedChanges } from '../../components/unsaved/unsaved';
import { formatDateTime } from '../../lib/format';
import { placeOf } from '../../lib/place';
import { colours, radius } from '../../theme';
import { chargerName } from './status';

/** The form takes text; the API schema turns it into numbers. */
const priceForm = tariffFields
  .omit({ chargerIds: true, startsAt: true })
  .extend({ idleOn: z.boolean(), when: z.enum(['now', 'later']), startsAtLocal: z.string(), wholeSite: z.boolean() })
  .superRefine((v, ctx) => {
    if ((v.infinityevRatePerKwh ?? 0) > v.pricePerKwh) ctx.addIssue({ code: 'custom', path: ['infinityevRatePerKwh'], message: 'At most the rate' });
    v.periodRates.forEach((r, i) => {
      if ((r.infinityevRatePerKwh ?? 0) > r.pricePerKwh) ctx.addIssue({ code: 'custom', path: ['periodRates', i, 'infinityevRatePerKwh'], message: 'At most the rate' });
    });
    if (!v.idleOn) return;
    if (v.idleFeePerMinute === null || v.idleFeePerMinute <= 0) ctx.addIssue({ code: 'custom', path: ['idleFeePerMinute'], message: 'Enter the idle fee, or turn it off' });
    else if ((v.infinityevIdleFeePerMinute ?? 0) > v.idleFeePerMinute) ctx.addIssue({ code: 'custom', path: ['infinityevIdleFeePerMinute'], message: 'At most the idle fee' });
    if (v.idleFeeMax !== null && v.idleFeeMax <= 0) ctx.addIssue({ code: 'custom', path: ['idleFeeMax'], message: 'More than 0, or leave it blank' });
  });
type In = z.input<typeof priceForm>;
type Out = z.output<typeof priceForm>;

/** yyyy-mm-ddThh:mm in local time, for a datetime-local input. */
const localNow = (plusMinutes = 0) => {
  const d = new Date(Date.now() + plusMinutes * 60_000);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};
const r4 = (n: number) => Math.round(n * 10_000) / 10_000;
const four = (n: number) => `$${n.toFixed(4)}`;
const num = (v: unknown) => {
  const n = Number(String(v ?? '').trim().replace(/^\$\s*/, ''));
  return String(v ?? '').trim() !== '' && Number.isFinite(n) ? n : null;
};

/**
 * Set a new price (admins), public or private: the rate, Infinity EV's share, and from those
 * the card fee (public prices only) and what the site owner is left with (read only, in grey).
 * With peak and off-peak hours on the site, a rate for each period. The same for the idle fee,
 * which can be turned off. Opens with the current price, and the standard share from the
 * server's settings.
 */
export function SetPriceDialog({ charger: c, kind = 'public', close }: { charger: Charger; kind?: TariffKind } & DialogProps) {
  const defaults = useTariffDefaults();
  if (!defaults.data) {
    return (
      <FormDialog title={`Set a new ${kind} price`} submitLabel="Set the price" onSubmit={() => undefined} onClose={close} busy error={defaults.error?.message}>
        <Typography color="textSecondary">Loading…</Typography>
      </FormDialog>
    );
  }
  return <PriceForm charger={c} kind={kind} std={defaults.data} close={close} />;
}

function PriceForm({ charger: c, kind, std, close }: { charger: Charger; kind: TariffKind; std: TariffDefaults } & DialogProps) {
  const all = useChargers();
  const set = useSetTariff();
  const toast = useToast();
  const [error, setError] = useState('');
  const others = (all.data ?? []).filter((x) => x.siteId === c.siteId && x.id !== c.id && x.registration === 'accepted');
  const isPublic = kind === 'public';
  const cardFeePercent = isPublic ? std.cardFeePercent : 0;
  const now = isPublic ? c.tariff : c.privateTariff;
  // The site's peak and off-peak hours: a rate for each period (the first covers all other times).
  const schedule = c.rateSchedule && c.rateSchedule.periods.length > 1 ? c.rateSchedule : null;
  const nowPeriods = now?.periods ?? null;
  // A lower share negotiated for this charger carries over; otherwise the standard one.
  const share = now?.infinityevRatePerKwh ?? std.infinityevRatePerKwh;
  const idleFee = now ? now.idleFeePerMinute : null;
  const idleShare = now?.infinityevIdleFeePerMinute ?? (idleFee ? r4((idleFee * std.infinityevIdlePercent) / 100) : null);
  const periodStart = (name: string) => nowPeriods?.find((p) => p.name === name);

  const form = useForm<In, unknown, Out>({
    resolver: zodResolver(priceForm),
    defaultValues: {
      kind,
      pricePerKwh: now ? now.pricePerKwh.toFixed(2) : '',
      infinityevRatePerKwh: share.toFixed(4),
      periodRates: (schedule?.periods.slice(1) ?? []).map((p) => {
        const was = periodStart(p.name);
        return { pricePerKwh: was ? was.pricePerKwh.toFixed(2) : '', infinityevRatePerKwh: (was?.infinityevRatePerKwh ?? share).toFixed(4) };
      }),
      idleOn: now ? !!idleFee : true,
      idleFeePerMinute: idleFee ? idleFee.toFixed(2) : '',
      infinityevIdleFeePerMinute: idleShare != null ? idleShare.toFixed(4) : '',
      idleGraceMinutes: String(now?.idleGraceMinutes ?? 15),
      idleFeeMax: now?.idleFeeMax != null ? now.idleFeeMax.toFixed(2) : '',
      gstInclusive: now?.gstInclusive ?? true,
      when: 'now',
      startsAtLocal: localNow(60),
      wholeSite: false,
    } as unknown as In,
  });
  const v = useWatch({ control: form.control });

  // Infinity EV's share of the idle fee follows the fee (INFINITYEV_IDLE_PERCENT) until someone types their own.
  const idleShareTyped = useRef(now?.infinityevIdleFeePerMinute != null);
  useEffect(() => {
    if (idleShareTyped.current) return;
    const fee = num(v.idleFeePerMinute);
    form.setValue('infinityevIdleFeePerMinute', (fee === null ? '' : r4((fee * std.infinityevIdlePercent) / 100).toFixed(4)) as never);
  }, [v.idleFeePerMinute, std.infinityevIdlePercent, form]);

  const submit = formSaver(form, async (x) => {
    setError('');
    if (x.when === 'later' && !x.startsAtLocal) {
      form.setError('startsAtLocal', { message: 'Choose when it starts' });
      return false;
    }
    const startsAt = x.when === 'later' ? new Date(x.startsAtLocal).toISOString() : null;
    const chargerIds = [c.id, ...(x.wholeSite ? others.map((o) => o.id) : [])];
    try {
      const r = await set.mutateAsync({
        chargerIds,
        kind,
        pricePerKwh: x.pricePerKwh,
        periodRates: x.periodRates,
        gstInclusive: x.gstInclusive,
        idleFeePerMinute: x.idleOn ? x.idleFeePerMinute : null,
        idleGraceMinutes: x.idleGraceMinutes,
        idleFeeMax: x.idleOn ? x.idleFeeMax : null,
        infinityevRatePerKwh: x.infinityevRatePerKwh,
        infinityevIdleFeePerMinute: x.idleOn ? x.infinityevIdleFeePerMinute : null,
        startsAt,
      });
      const where = r.count > 1 ? `${r.count} chargers at ${placeOf(c)}` : chargerName(c);
      const who = isPublic ? 'guests' : 'RFID cards';
      toast(startsAt ? `The new ${kind} price starts on ${formatDateTime(startsAt)} for ${where}.` : schedule ? `${where}: new ${kind} prices are in force.` : `${where}: ${who} now pay ${formatRate(x.pricePerKwh)} per kWh.`);
      form.reset(undefined, { keepValues: true });
      close();
    } catch (e) {
      const msg = applyApiError(e, form.setError, ['pricePerKwh', 'infinityevRatePerKwh', 'idleFeePerMinute', 'infinityevIdleFeePerMinute', 'idleGraceMinutes', 'idleFeeMax', ...(schedule?.periods.slice(1) ?? []).flatMap((_, i) => [`periodRates.${i}.pricePerKwh`, `periodRates.${i}.infinityevRatePerKwh`])]);
      // The API names the start time "startsAt".
      if (e instanceof ApiError && e.fields.startsAt) form.setError('startsAtLocal', { message: e.fields.startsAt });
      setError(e instanceof ApiError && e.fields.startsAt && Object.keys(e.fields).length === 1 ? '' : msg);
      return false;
    }
  });
  useUnsavedChanges({ dirty: form.formState.isDirty, save: submit, discard: () => form.reset() });

  const price = num(v.pricePerKwh);
  const priceShare = num(v.infinityevRatePerKwh);
  const fee = num(v.idleFeePerMinute);
  const feeShare = num(v.infinityevIdleFeePerMinute);
  const split = price !== null && priceShare !== null ? priceSplit(price, priceShare, cardFeePercent) : null;
  const idleSplit = v.idleOn && fee !== null && feeShare !== null ? priceSplit(fee, feeShare, cardFeePercent) : null;
  // Rate, Infinity EV, card fees (public only) and remittance: the same columns on every row.
  const columns = isPublic ? 4 : 3;
  const splitOf = (i: number) => {
    const p = num(v.periodRates?.[i]?.pricePerKwh);
    const s = num(v.periodRates?.[i]?.infinityevRatePerKwh);
    return p !== null && s !== null ? priceSplit(p, s, cardFeePercent) : null;
  };
  const shareHint =
    priceShare === null || r4(priceShare) === r4(std.infinityevRatePerKwh)
      ? 'Standard rate'
      : priceShare < std.infinityevRatePerKwh
        ? `Lower than the standard ${four(std.infinityevRatePerKwh)}`
        : `More than the standard ${four(std.infinityevRatePerKwh)}`;

  return (
    <FormDialog
      title={`Set a new ${kind} price`}
      lead={`${chargerName(c)} · ${placeOf(c)}. ${isPublic ? 'What guests pay to charge.' : 'What this organisation’s RFID cards are charged.'} Prices that have started are never changed, so past sessions keep theirs.`}
      submitLabel={set.isPending ? 'Saving…' : 'Set the price'}
      onSubmit={submit}
      onClose={close}
      busy={set.isPending}
      error={error}
      maxWidth="md"
      width={850}
    >
      <Box sx={{ display: 'grid', gap: 3 }}>
        <Section columns={columns} title={schedule ? `Price per kWh: ${schedule.name}` : 'Price per kWh'}>
          {schedule && <PeriodLabel name={schedule.periods[0]!.name} hours="All other times" />}
          <Money control={form.control} name="pricePerKwh" label={`${isPublic ? 'Public' : 'Private'} rate ($ per kWh)`} placeholder="e.g. 0.29" />
          <Money control={form.control} name="infinityevRatePerKwh" label="Infinity EV rate" helperText={shareHint} />
          {isPublic && <ReadOnly label={`Card fees (${std.cardFeePercent}%)`} value={split ? four(split.cardFee) : '—'} />}
          <ReadOnly label="Final remittance" value={split ? four(split.remittance) : '—'} helperText="To the site owner" tone={split && split.remittance < 0 ? 'bad' : undefined} />
          {schedule?.periods.slice(1).map((p, i) => {
            const ps = splitOf(i);
            return (
              <Box key={p.name} sx={{ display: 'contents' }}>
                <PeriodLabel name={p.name} hours={p.windows.map(describeWindow).join('; ')} />
                <Money control={form.control} name={`periodRates.${i}.pricePerKwh`} label={`${p.name} rate`} placeholder="e.g. 0.45" />
                <Money control={form.control} name={`periodRates.${i}.infinityevRatePerKwh`} label="Infinity EV rate" />
                {isPublic && <ReadOnly label={`Card fees (${std.cardFeePercent}%)`} value={ps ? four(ps.cardFee) : '—'} />}
                <ReadOnly label="Final remittance" value={ps ? four(ps.remittance) : '—'} helperText="To the site owner" tone={ps && ps.remittance < 0 ? 'bad' : undefined} />
              </Box>
            );
          })}
        </Section>

        <Box>
          <Controller
            control={form.control}
            name="idleOn"
            render={({ field }) => (
              <FormControlLabel control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Idle fee: for staying plugged in after charging stops" />
            )}
          />
          {v.idleOn && (
            <Box sx={{ display: 'grid', gap: 2 }}>
            <Section columns={columns}>
              <Money control={form.control} name="idleFeePerMinute" label="Idle fee ($ per minute)" placeholder="e.g. 0.90" />
              <Money
                control={form.control}
                name="infinityevIdleFeePerMinute"
                label="Infinity EV share"
                helperText={`Usually ${std.infinityevIdlePercent}% of the fee`}
                onTyped={() => (idleShareTyped.current = true)}
              />
              {isPublic && <ReadOnly label={`Card fees (${std.cardFeePercent}%)`} value={idleSplit ? four(idleSplit.cardFee) : '—'} />}
              <ReadOnly label="Final remittance" value={idleSplit ? four(idleSplit.remittance) : '—'} helperText="To the site owner" tone={idleSplit && idleSplit.remittance < 0 ? 'bad' : undefined} />
            </Section>
            {/* On their own line, a column each, so the rows above line up. */}
            <Section columns={columns}>
              <Controller
                control={form.control}
                name="idleGraceMinutes"
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    label="Idle grace period"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message ?? 'Free minutes first'}
                    slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                  />
                )}
              />
              <Money
                control={form.control}
                name="idleFeeMax"
                label="Maximum idle fee ($ a session)"
                placeholder={isPublic && fee ? (fee * IDLE_ALLOWANCE_MINUTES).toFixed(2) : 'No limit'}
                helperText={isPublic ? `Blank: an hour's worth${fee ? ` (${formatRate(fee * IDLE_ALLOWANCE_MINUTES)})` : ''}` : 'Blank: no limit'}
                sx={{ gridColumn: 'span 2' }}
              />
            </Section>
            </Box>
          )}
        </Box>

        <Controller
          control={form.control}
          name="gstInclusive"
          render={({ field }) => <FormControlLabel control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="The price includes GST" />}
        />
        <Controller
          control={form.control}
          name="when"
          render={({ field }) => (
            <RadioGroup row value={field.value} onChange={(e) => field.onChange(e.target.value)} aria-label="When it starts">
              <FormControlLabel value="now" control={<Radio />} label="Starts now" />
              <FormControlLabel value="later" control={<Radio />} label="Schedule for later" />
            </RadioGroup>
          )}
        />
        {v.when === 'later' && (
          <Controller
            control={form.control}
            name="startsAtLocal"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type="datetime-local"
                label="Starts"
                error={!!fieldState.error}
                helperText={fieldState.error?.message ?? 'Sessions that start before then pay the current price'}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: localNow() } }}
                sx={{ maxWidth: { sm: 320 } }}
              />
            )}
          />
        )}
        {others.length > 0 && (
          <Controller
            control={form.control}
            name="wholeSite"
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                label={`Also set it on the other ${others.length === 1 ? 'charger' : `${others.length} chargers`} at ${placeOf(c)}`}
              />
            )}
          />
        )}
      </Box>
    </FormDialog>
  );
}

/** The period a row of rates is for, across the whole row. */
function PeriodLabel({ name, hours }: { name: string; hours: string }) {
  return (
    <Typography sx={{ gridColumn: '1 / -1', fontWeight: 500, mt: 1, mb: -1 }}>
      {name} <Typography component="span" variant="body2" color="textSecondary">{hours}</Typography>
    </Typography>
  );
}

/** A row of the price grid: the same columns on every row (4 public, 3 private), two a row on phones. */
function Section({ title, columns, children }: { title?: string; columns: number; children: React.ReactNode }) {
  return (
    <Box>
      {title && <Typography sx={{ fontWeight: 500, mb: 1.5 }}>{title}</Typography>}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr 1fr', md: `repeat(${columns}, 1fr)` }, alignItems: 'start' }}>{children}</Box>
    </Box>
  );
}

type MoneyField = 'pricePerKwh' | 'infinityevRatePerKwh' | 'idleFeePerMinute' | 'infinityevIdleFeePerMinute' | 'idleFeeMax' | `periodRates.${number}.pricePerKwh` | `periodRates.${number}.infinityevRatePerKwh`;

function Money({ control, name, label, placeholder, helperText, onTyped, sx }: { control: Control<In>; name: MoneyField; label: string; placeholder?: string; helperText?: string; onTyped?(): void; sx?: SxProps<Theme> }) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(e) => {
            onTyped?.();
            field.onChange(e.target.value);
          }}
          label={label}
          placeholder={placeholder}
          error={!!fieldState.error}
          helperText={fieldState.error?.message ?? helperText ?? ' '}
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          sx={sx}
        />
      )}
    />
  );
}

/** Worked out from the others: grey, not editable. */
function ReadOnly({ label, value, helperText, tone }: { label: string; value: string; helperText?: string; tone?: 'bad' }) {
  return (
    <TextField
      label={label}
      value={value}
      helperText={tone === 'bad' ? 'Less than nothing: check the rates' : (helperText ?? ' ')}
      error={tone === 'bad'}
      tabIndex={-1}
      slotProps={{ htmlInput: { readOnly: true, tabIndex: -1, 'aria-readonly': true }, inputLabel: { shrink: true } }}
      sx={{
        '& .MuiOutlinedInput-root': { bgcolor: colours.paleGrey, borderRadius: `${radius.button}px` },
        '& .MuiOutlinedInput-input': { color: colours.graphite },
        '& .MuiOutlinedInput-notchedOutline': { borderStyle: 'dashed' },
      }}
    />
  );
}
